import React from 'react';
import { cn } from '../lib/cn';

/**
 * Icon, the single icon primitive.
 *
 * Source doctrine: DESKTOP-PREFERENCES.md section 6b.
 *
 *   REGULAR IS RESTING, FILL IS ACTIVE.
 *
 * Same glyph, swapped to fill only when the thing it belongs to is active or
 * selected: a wishlisted card, a cart holding items, the active nav or sidebar
 * item, the active tab, a chosen filter, a completed step, a followed store.
 * Never fill on hover, hover is a reveal and fill is a commitment. Never fill
 * decoratively, it is a state signal in the same family as the accent rule.
 *
 * Star ratings are the one nuance and are handled by Rating, not here: the
 * earned portion is filled and the remainder regular, and stars stay ink
 * coloured, never yellow.
 *
 * Why `as` and not a name string: passing the component keeps the icon set
 * tree-shakeable. A `name` lookup table would pull all ~1500 Phosphor glyphs
 * into the bundle.
 *
 *   import { Heart } from '@phosphor-icons/react';
 *   <Icon as={Heart} label="Add to wishlist" />
 *   <Icon as={Heart} active />
 *
 * Sizing note: Phosphor React renders at `size="1em"`, so the vendored
 * `.dsk-icon { font-size: 18px }` rules size the SVG exactly as they sized the
 * icon font in the HTML templates. The `.ph` / `.ph-fill` class names are kept
 * on the element so every existing rule that reaches through them keeps working,
 * for example `.dsk-btn--primary .ph { color: inherit }`.
 */
export function Icon({
  as: Glyph,
  active = false,
  size = 'md',
  label,
  className,
  ...rest
}) {
  if (!Glyph) return null;

  const sizeClass = {
    sm: 'dsk-icon--sm',
    md: null,
    lg: 'dsk-icon--lg',
    xl: 'dsk-icon--xl',
  }[size];

  // An icon with a label is meaningful and needs a name. Without one it is
  // decorative and must be hidden from assistive tech entirely.
  const a11y = label
    ? { role: 'img', 'aria-label': label }
    : { 'aria-hidden': 'true', focusable: 'false' };

  return (
    <Glyph
      weight={active ? 'fill' : 'regular'}
      size="1em"
      className={cn('dsk-icon', active ? 'ph-fill' : 'ph', sizeClass, className)}
      {...a11y}
      {...rest}
    />
  );
}

export default Icon;
